import { Navigate, useRoutes } from 'react-router-dom';
// auth
import AuthGuard from '@raas/shared/src/auth/AuthGuard';
import GuestGuard from '@raas/shared/src/auth/GuestGuard';
// layouts
import CompactLayout from '@raas/shared/src/layouts/compact';
import { LegalProvisionContextProvider } from '@raas/shared/src/contexts/LegalProvisionContextProvider';
import { DeltaContextProvider } from '@raas/shared/src/contexts/DeltaContextProvider';
import { UserContextProvider } from '@raas/shared/src/features/user/contexts/UserContextProvider';
import { SubscriptionContextProvider } from '@raas/shared/src/features/subscription/contexts/SubscriptionContextProvider';
import { RegulationContextProvider } from '@raas/shared/src/features/regulation/contexts/RegulationContextProvider';
import { RegulationListContextProvider } from '@raas/shared/src/features/regulation/contexts/RegulationListContextProvider';
import { RegulationMyListContextProvider } from '@raas/shared/src/features/regulation/contexts/RegulationMyListContextProvider';
import { BundlingContextProvider } from '@raas/shared/src/features/bundling/contexts/BundlingContextProvider';
import { MyOrganizationProvider } from '@raas/shared/src/features/organization/contexts/MyOrganizationProvider';
import { OrganizationProvider } from '@raas/shared/src/features/organization/contexts/OrganizationProvider';
import DashboardLayout from '../layouts/dashboard';
// config
import { PATH_AFTER_LOGIN } from '../config-global';
//
import {
  DocumentElementCreatePage,
  DocumentElementEditPage,
  DocumentElementViewPage,
  LegalProvisionCreatePage,
  LegalProvisionDetailsPage,
  LegalProvisionEditPage,
  LegalProvisionNewVersionPage,
  LegalProvisionListPage,
  LegalProvisionAccessPage,
  LegalRequirementCreatePage,
  LegalRequirementEditPage,
  LegalRequirementListPage,
  LoginPage,
  Page404,
  PropertyCreatePage,
  PropertyEditPage,
  PropertyListPage,
  SentenceFragmentsPage,
  TechnicalRequirementCreatePage,
  TechnicalRequirementEditPage,
  InputListPage,
  InputEditPage,
  InputCreatePage,
  TagGroupListPage,
  TagGroupEditPage,
  TagGroupCreatePage,
  TagListPage,
  TagEditPage,
  TagCreatePage,
  ReleaseCreatePage,
  TechnicalRequirementListPage,
  UserChangePassword,
  AlertsEditPage,
  AlertsListPage,
  ExportsListPage,
  ExportsEditPage,
  ReportsListPage,
  ImportPage,
  LegalRadarPage,
  LegalProvisionWithTranslationPage,
  RegulationListPage,
  RegulationPropertyListPage,
  DeltaPage,
  AdminPage,
  IntegrationSettingsPage,
  IntegrationPage,
  UserProfilePage,
  SubscriptionCreatePage,
  EditSubscriptionPage,
  BundlingListPage,
  BundlingCreatePage,
  BundlingEditPage,
  AttributeListPage,
  RegulationContentPage,
  RegulationAccessCreatePage,
  OrganizationListPage,
  OrganizationPage,
  OrganizationGroupListPage,
  OrganizationGroupPage,
  OrganizationMemberListPage,
  OrganizationGroupMemberListPage,
  OrganizationGroupRegulationListPage,
  AccessRegulationCreatePage,
} from './elements';

// ----------------------------------------------------------------------

export default function Router() {
  return useRoutes([
    {
      path: '/',
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        {
          path: 'login',
          element: (
            <GuestGuard>
              <LoginPage requiredPermission="INTERNAL_ACCESS" />
            </GuestGuard>
          ),
        },
      ],
    },
    {
      path: '/dashboard',
      element: (
        <AuthGuard requiredPermission="INTERNAL_ACCESS">
          <LegalProvisionContextProvider requiredPermission="INTERNAL_ACCESS">
            <UserContextProvider interfaceType="INTERNAL_ACCESS">
              <OrganizationProvider>
                <MyOrganizationProvider>
                  <RegulationListContextProvider>
                    <RegulationMyListContextProvider>
                      <RegulationContextProvider>
                        <BundlingContextProvider>
                          <SubscriptionContextProvider>
                            <DeltaContextProvider>
                              <DashboardLayout />
                            </DeltaContextProvider>
                          </SubscriptionContextProvider>
                        </BundlingContextProvider>
                      </RegulationContextProvider>
                    </RegulationMyListContextProvider>
                  </RegulationListContextProvider>
                </MyOrganizationProvider>
              </OrganizationProvider>
            </UserContextProvider>
          </LegalProvisionContextProvider>
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        {
          path: 'legal-provision',
          children: [
            { element: <Navigate to="/dashboard/legal-provision/list" replace />, index: true },
            { path: 'list', element: <LegalProvisionListPage /> },
            { path: 'new', element: <LegalProvisionCreatePage /> },
            { path: ':id/edit', element: <LegalProvisionEditPage /> },
            { path: ':id/new', element: <LegalProvisionNewVersionPage /> },
            { path: ':id/details', element: <LegalProvisionDetailsPage /> },
            { path: ':entityId/manage-access', element: <LegalProvisionAccessPage /> },
            { path: ':id/translation', element: <LegalProvisionWithTranslationPage /> },
          ],
        },
        {
          path: 'regulation',
          children: [
            { element: <Navigate to="/dashboard/regulation/list" replace />, index: true },
            { path: 'list', element: <RegulationListPage /> },
            { path: ':id/properties', element: <RegulationPropertyListPage /> },
            { path: ':id/tree', element: <LegalProvisionDetailsPage /> },
            { path: ':id/content', element: <RegulationContentPage /> },
            { path: ':id/access', element: <AccessRegulationCreatePage /> },
          ],
        },
        {
          path: 'translation',
          children: [
            {
              element: <Navigate to="/dashboard/translation/current-legal-provision" replace />,
              index: true,
            },
            { path: 'current-legal-provision', element: <LegalProvisionWithTranslationPage /> },
          ],
        },
        {
          path: 'document-element',
          children: [
            { path: 'new', element: <DocumentElementCreatePage /> },
            { path: ':id/edit', element: <DocumentElementEditPage /> },
            { path: ':id/view', element: <DocumentElementViewPage /> },
          ],
        },
        {
          path: 'sentence-fragments/:id',
          element: <SentenceFragmentsPage />,
        },
        {
          path: 'legal-requirement',
          children: [
            { element: <Navigate to="/dashboard/legal-requirement/list" replace />, index: true },
            { path: 'list', element: <LegalRequirementListPage /> },
            { path: 'new', element: <LegalRequirementCreatePage /> },
            { path: ':id/edit', element: <LegalRequirementEditPage /> },
          ],
        },
        {
          path: 'property',
          children: [
            { element: <Navigate to="/dashboard/property/list" replace />, index: true },
            { path: 'list', element: <PropertyListPage /> },
            { path: 'new', element: <PropertyCreatePage /> },
            { path: ':id/edit', element: <PropertyEditPage /> },
          ],
        },
        { path: 'legal-radar', element: <LegalRadarPage /> },
        {
          path: 'technical-requirement',
          children: [
            {
              element: <Navigate to="/dashboard/technical-requirement/list" replace />,
              index: true,
            },
            { path: 'list', element: <TechnicalRequirementListPage /> },
            { path: 'new', element: <TechnicalRequirementCreatePage /> },
            { path: ':id/edit', element: <TechnicalRequirementEditPage /> },
          ],
        },
        {
          path: 'input',
          children: [
            {
              element: <Navigate to="/dashboard/input/list" replace />,
              index: true,
            },
            { path: 'list', element: <InputListPage /> },
            { path: 'new', element: <InputCreatePage /> },
            { path: ':id/edit', element: <InputEditPage /> },
          ],
        },
        {
          path: 'tag-group',
          children: [
            {
              element: <Navigate to="/dashboard/tag-group/list" replace />,
              index: true,
            },
            { path: 'list', element: <TagGroupListPage /> },
            { path: 'new', element: <TagGroupCreatePage /> },
            { path: ':id/edit', element: <TagGroupEditPage /> },
          ],
        },
        {
          path: 'tag',
          children: [
            {
              element: <Navigate to="/dashboard/tag/list" replace />,
              index: true,
            },
            { path: 'list', element: <TagListPage /> },
            { path: 'new', element: <TagCreatePage /> },
            { path: ':id/edit', element: <TagEditPage /> },
          ],
        },
        {
          path: 'release',
          children: [{ path: ':id/:versionId/new', element: <ReleaseCreatePage /> }],
        },
        {
          path: 'user',
          children: [
            { path: 'change-password', element: <UserChangePassword /> },
            { path: 'profile', element: <UserProfilePage /> },
          ],
        },
        {
          path: 'delta',
          children: [
            {
              element: <Navigate to="/dashboard/delta/lp" replace />,
              index: true,
            },
            {
              path: 'lp',
              element: <DeltaPage />,
            },
          ],
        },
        {
          path: 'alerts',
          children: [
            {
              path: 'list',
              element: <AlertsListPage />,
            },
            {
              path: 'new',
              element: <AlertsEditPage create />,
            },
            {
              path: ':id/edit',
              element: <AlertsEditPage create={false} />,
            },
          ],
        },
        {
          path: 'exports',
          children: [
            {
              path: 'list',
              element: <ExportsListPage />,
            },
            {
              path: 'new',
              element: <ExportsEditPage create />,
            },
            {
              path: ':id/edit',
              element: <ExportsEditPage create={false} />,
            },
          ],
        },
        {
          path: 'reports',
          children: [
            {
              path: 'list',
              element: <ReportsListPage />,
            },
          ],
        },
        {
          path: 'import',
          element: <ImportPage />,
        },
        {
          path: 'admins',
          element: <AdminPage />,
        },
        {
          path: 'integration',
          children: [
            {
              path: 'search',
              element: <IntegrationPage />,
            },
            {
              path: 'settings',
              element: <IntegrationSettingsPage />,
            },
          ],
        },
        {
          path: 'subscription',
          children: [
            {
              path: 'create',
              element: <SubscriptionCreatePage />,
            },
            {
              path: ':id/edit',
              element: <EditSubscriptionPage />,
            },
          ],
        },
        {
          path: 'bundlings',
          children: [
            {
              element: <Navigate to="/dashboard/bundlings/list" replace />,
              index: true,
            },
            {
              path: 'list',
              element: <BundlingListPage />,
            },
            {
              path: 'new',
              element: <BundlingCreatePage />,
            },
            {
              path: ':id/edit',
              element: <BundlingEditPage />,
            },
          ],
        },
        {
          path: 'attributes',
          children: [
            {
              element: <Navigate to="/dashboard/attributes/list" replace />,
              index: true,
            },
            {
              path: 'list',
              element: <AttributeListPage />,
            },
          ],
        },
        {
          path: 'access',
          children: [{ path: 'new', element: <RegulationAccessCreatePage /> }],
        },
        {
          path: 'organization',
          children: [
            {
              element: <Navigate to="/dashboard/organization/list" replace />,
              index: true,
            },
            { path: 'list', element: <OrganizationListPage /> },
            { path: ':id', element: <OrganizationPage /> },
            { path: ':id/group/list', element: <OrganizationGroupListPage /> },
            { path: ':id/group/:groupId', element: <OrganizationGroupPage /> },
            { path: ':id/member/list', element: <OrganizationMemberListPage /> },
            {
              path: ':id/group/:groupId/member/list',
              element: <OrganizationGroupMemberListPage />,
            },
            {
              path: ':id/group/:groupId/regulation/list',
              element: <OrganizationGroupRegulationListPage />,
            },
          ],
        },
      ],
    },
    {
      element: <CompactLayout />,
      children: [{ path: '404', element: <Page404 /> }],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}
